import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export interface ApiClientConfig {
  baseURL: string;
  getAccessToken?: () => string | undefined;
  setAccessToken?: (accessToken: string) => void;
  onUnauthorized?: () => void | Promise<void>;
}

let config: ApiClientConfig;
let axiosInstance: AxiosInstance = axios.create();
let refreshClient: AxiosInstance = axios.create();
let refreshPromise: Promise<string> | null = null;
let isHandlingUnauthorized = false;

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

type ErrorResponseBody = {
  statusCode?: string;
};

const isAuthLoginRequest = (url?: string) => url?.includes('/auth/login');
const isAuthRefreshRequest = (url?: string) => url?.includes('/auth/refresh-token');
const EXPIRED_ACCESS_TOKEN_STATUS_CODE = '10004';
const INVALID_ACCESS_TOKEN_STATUS_CODE = '10003';
const TOKEN_EXPIRED_REASON = 'TOKEN_EXPIRED';
const REFRESH_TOKEN_INSTRUCTION = 'refresh_token';

const getHeaderValue = (headers: AxiosError['response'] extends infer R
  ? R extends { headers?: infer H }
    ? H
    : never
  : never, key: string) => {
  if (!headers) {
    return undefined;
  }

  if (typeof (headers as Record<string, unknown>)['get'] === 'function') {
    return (headers as { get: (name: string) => string | undefined }).get(key);
  }

  const normalizedHeaders = headers as Record<string, string | undefined>;
  return normalizedHeaders[key] ?? normalizedHeaders[key.toLowerCase()];
};

const shouldRefreshAccessToken = (error: AxiosError<ErrorResponseBody>) => {
  if (error.response?.status !== 401) {
    return false;
  }

  const statusCode = error.response?.data?.statusCode;
  const reason = getHeaderValue(error.response?.headers, 'reason');
  const instruction = getHeaderValue(error.response?.headers, 'instruction');

  if (statusCode === INVALID_ACCESS_TOKEN_STATUS_CODE) {
    return false;
  }

  return (
    statusCode === EXPIRED_ACCESS_TOKEN_STATUS_CODE ||
    reason === TOKEN_EXPIRED_REASON ||
    instruction === REFRESH_TOKEN_INSTRUCTION
  );
};

const handleUnauthorized = async () => {
  if (isHandlingUnauthorized) {
    return;
  }

  isHandlingUnauthorized = true;

  try {
    await config.onUnauthorized?.();
  } finally {
    isHandlingUnauthorized = false;
  }
};

const getRefreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post<ApiResponse<{ accessToken: string }>>('/auth/refresh-token')
      .then(response => {
        const accessToken = response.data.data.data.accessToken;
        config.setAccessToken?.(accessToken);
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export function initApiClient(cfg: ApiClientConfig) {
  config = cfg;

  axiosInstance = axios.create({
    baseURL: config.baseURL + '/api/v1',
    withCredentials: true,
  });

  refreshClient = axios.create({
    baseURL: config.baseURL + '/api/v1',
    withCredentials: true,
  });

  axiosInstance.interceptors.request.use(
    request => {
      const accessToken = config.getAccessToken?.();

      if (accessToken) {
        request.headers.Authorization = `Bearer ${accessToken}`;
      }

      return request;
    },
    error => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError<ErrorResponseBody>) => {
      const originalRequest = error.config as RetryableRequestConfig | undefined;
      const recoverable401 = shouldRefreshAccessToken(error);

      if (
        recoverable401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthLoginRequest(originalRequest.url) &&
        !isAuthRefreshRequest(originalRequest.url)
      ) {
        originalRequest._retry = true;

        try {
          const accessToken = await getRefreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          await handleUnauthorized();
          return Promise.reject(refreshError);
        }
      }

      if (error.response?.status === 401 && !recoverable401 && !isAuthLoginRequest(originalRequest?.url)) {
        await handleUnauthorized();
      }

      return Promise.reject(error);
    },
  );
}

export type DataResponse<T> = {
  data: T;
  totalItem?: number;
};

type ApiResponse<T> = {
  data: DataResponse<T>;
};

const getRequest = async <T>(endpoint: string, params?: unknown): Promise<DataResponse<T>> => {
  const res = await axiosInstance.get<ApiResponse<T>>(endpoint, {
    params: params ?? {},
  });

  return res.data.data;
};

const postRequest = async <T>(endpoint: string, payload: unknown): Promise<DataResponse<T>> => {
  const res = await axiosInstance.post<ApiResponse<T>>(endpoint, payload);
  return res.data.data;
};

const putRequest = async <T>(endpoint: string, payload: unknown): Promise<DataResponse<T>> => {
  const res = await axiosInstance.put<ApiResponse<T>>(endpoint, payload);
  return res.data.data;
};

const patchRequest = async <T>(endpoint: string, payload: unknown): Promise<DataResponse<T>> => {
  const res = await axiosInstance.patch<ApiResponse<T>>(endpoint, payload);
  return res.data.data;
};

const deleteRequest = async <T>(endpoint: string): Promise<DataResponse<T>> => {
  const res = await axiosInstance.delete<ApiResponse<T>>(endpoint);
  return res.data.data;
};

const getApiAccessToken = () => config?.getAccessToken?.() ?? '';

export { config, axiosInstance, putRequest, getRequest, postRequest, patchRequest, deleteRequest, getApiAccessToken };
